import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  PlayIcon,
  StopIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SignalIcon,
  BeakerIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const timeAgo = (value) => {
  if (!value) return 'never';
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const phaseBadge = (phase, success) => {
  if (phase === 'phase2') {
    return success === false
      ? { label: 'Phase 2 failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
      : { label: 'Phase 2', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
  }
  if (phase === 'phase1') {
    return { label: 'Phase 1 only', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' };
  }
  return { label: 'Not scanned', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' };
};

function MiniStat({ label, value, sub, variant = 'slate' }) {
  const variants = {
    indigo: 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20',
    emerald: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20',
    amber: 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20',
    red: 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20',
    slate: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
  };
  return (
    <div className={`rounded-xl border p-4 ${variants[variant] || variants.slate}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
        {Number(value || 0).toLocaleString()}
      </div>
      {sub && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}

function fmtStat(value) {
  if (value == null || value === '') return '—';
  return Number(value).toLocaleString();
}

function fmtDelta(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n > 0) return `+${n.toLocaleString()}`;
  if (n === 0) return '+0';
  return n.toLocaleString();
}

function deltaClass(value) {
  if (value == null || value === '') return 'text-gray-400';
  const n = Number(value);
  if (n > 0) return 'text-emerald-600 dark:text-emerald-400 font-medium';
  if (n === 0) return 'text-gray-500 dark:text-gray-400';
  return 'text-red-500';
}

function ScanJobProgressModal({ job, onClose, onRefreshTable }) {
  const isTest = job?.kind === 'test';
  const isDone = job?.status === 'completed' || job?.status === 'failed';
  const phase2Total = isTest ? (job?.phase2Triggered ?? 0) : (job?.accountsTotal ?? 0);
  const progressNum = isTest && job?.currentPhase === 'phase1'
    ? (job?.phase1Checked ?? 0)
    : (job?.accountsScanned ?? 0);
  const progressDen = isTest && job?.currentPhase === 'phase1'
    ? (job?.accountsTotal ?? 0)
    : phase2Total;
  const pct = progressDen > 0
    ? Math.min(100, Math.round((progressNum / progressDen) * 100))
    : (isDone ? 100 : 0);

  const title = isTest
    ? `Test scan (P1→P2) — ${job?.agents?.[0]?.name || 'agent'}`
    : job?.scope === 'agent'
      ? `Phase 2 scan — ${job?.agents?.[0]?.name || 'agent'}`
      : 'Phase 2 scan — all agents';

  const statusLine = (() => {
    if (job?.status === 'queued') return 'Queued…';
    if (job?.status === 'failed') return 'Scan failed';
    if (job?.status === 'completed') return 'Scan complete';
    if (isTest && job?.currentPhase === 'phase1') return 'Phase 1 — fetching following / followers…';
    if (isTest && job?.currentPhase === 'phase2') return 'Phase 2 — scraping follower lists where count increased…';
    return 'Scanning follower lists…';
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={isDone ? onClose : undefined} aria-hidden="true" />
      <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{statusLine}</p>
          </div>
          {isDone && (
            <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="px-5 py-4 space-y-4">
          {!isDone && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                <span>{isTest && job?.currentPhase === 'phase1' ? 'Phase 1 checked' : 'Phase 2 scanned'}</span>
                <span className="tabular-nums">
                  {progressNum.toLocaleString()} / {progressDen.toLocaleString()}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ease-out ${isTest ? 'bg-amber-500' : 'bg-indigo-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}

          {isTest && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-3">
                <div className="text-xs text-amber-700 dark:text-amber-300">Phase 1 checked</div>
                <div className="text-xl font-bold tabular-nums text-amber-800 dark:text-amber-200 mt-1">
                  {(job?.phase1Checked ?? 0).toLocaleString()}
                  <span className="text-sm font-normal text-amber-500"> / {(job?.accountsTotal ?? 0).toLocaleString()}</span>
                </div>
              </div>
              <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-3">
                <div className="text-xs text-indigo-700 dark:text-indigo-300">Phase 2 triggered</div>
                <div className="text-xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300 mt-1">
                  {(job?.phase2Triggered ?? 0).toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Total following (P1)</div>
                <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-white mt-1">
                  {fmtStat(job?.totalFollowing)}
                </div>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Total followers (P1)</div>
                <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-white mt-1">
                  {fmtStat(job?.totalFollowers)}
                </div>
              </div>
              {(job?.totalFollowingDelta != null || job?.totalFollowersDelta != null) && (
                <>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Δ Following (P1 sum)</div>
                    <div className={`text-xl font-bold tabular-nums mt-1 ${deltaClass(job?.totalFollowingDelta)}`}>
                      {fmtDelta(job?.totalFollowingDelta)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Δ Followers (P1 sum)</div>
                    <div className={`text-xl font-bold tabular-nums mt-1 ${deltaClass(job?.totalFollowersDelta)}`}>
                      {fmtDelta(job?.totalFollowersDelta)}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Accounts scanned</div>
              <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-white mt-1">
                {(job?.accountsScanned ?? 0).toLocaleString()}
                <span className="text-sm font-normal text-gray-400"> / {(job?.accountsTotal ?? 0).toLocaleString()}</span>
              </div>
            </div>
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 p-3">
              <div className="text-xs text-emerald-700 dark:text-emerald-300">Successful scans</div>
              <div className="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300 mt-1">
                {(job?.scansSuccess ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20 p-3">
              <div className="text-xs text-red-700 dark:text-red-300">Failed scans</div>
              <div className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400 mt-1">
                {(job?.scansFailed ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-3">
              <div className="text-xs text-indigo-700 dark:text-indigo-300">Ledger follow-backs</div>
              <div className="text-xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300 mt-1">
                {fmtStat(job?.ledgerFollowBacks)}
              </div>
            </div>
            <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50/50 dark:bg-sky-950/20 p-3">
              <div className="text-xs text-sky-700 dark:text-sky-300">Organic followers</div>
              <div className="text-xl font-bold tabular-nums text-sky-700 dark:text-sky-300 mt-1">
                {fmtStat(job?.organicFollowers)}
              </div>
            </div>
            <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 p-3 col-span-2">
              <div className="text-xs text-purple-700 dark:text-purple-300">DMs sent (ledger / organic)</div>
              <div className="text-xl font-bold tabular-nums text-purple-700 dark:text-purple-300 mt-1">
                {fmtStat((job?.dmsSentLedger || 0) + (job?.dmsSentOrganic || 0))}
                <span className="text-sm font-normal text-purple-400 ml-2">
                  ledger {fmtStat(job?.dmsSentLedger)} · organic {fmtStat(job?.dmsSentOrganic)}
                </span>
              </div>
            </div>
            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-3 col-span-2">
              <div className="text-xs text-amber-700 dark:text-amber-300">DMs failed (ledger / organic)</div>
              <div className="text-xl font-bold tabular-nums text-amber-700 dark:text-amber-300 mt-1">
                {fmtStat((job?.dmsFailedLedger || 0) + (job?.dmsFailedOrganic || 0))}
                <span className="text-sm font-normal text-amber-500 ml-2">
                  ledger {fmtStat(job?.dmsFailedLedger)} · organic {fmtStat(job?.dmsFailedOrganic)}
                </span>
              </div>
            </div>
          </div>

          {job?.scope === 'all' && (job?.agents || []).some((a) => a.ledgerFollowBacks != null || a.accountsScanned) && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40">
                Per agent
              </div>
              <table className="min-w-full text-xs">
                <thead className="text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium">Agent</th>
                    <th className="text-right py-2 px-3 font-medium">Ledger</th>
                    <th className="text-right py-2 px-3 font-medium">Organic</th>
                    <th className="text-right py-2 px-3 font-medium">DMs OK</th>
                    <th className="text-right py-2 px-3 font-medium">DMs fail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                  {(job.agents || []).map((a) => (
                    <tr key={a.agentId}>
                      <td className="py-2 px-3 text-gray-900 dark:text-white">{a.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtStat(a.ledgerFollowBacks)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtStat(a.organicFollowers)}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-emerald-600">
                        {fmtStat((a.dmsSentLedger || 0) + (a.dmsSentOrganic || 0))}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-red-500">
                        {fmtStat((a.dmsFailedLedger || 0) + (a.dmsFailedOrganic || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {job?.status === 'failed' && job?.error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              {job.error}
            </div>
          )}

          {job?.status === 'completed' && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircleIcon className="w-5 h-5" />
              {isTest
                ? `Test scan finished — P1 ${(job?.phase1Checked ?? 0).toLocaleString()}, P2 ${(job?.phase2Triggered ?? 0).toLocaleString()} triggered, following ${fmtStat(job?.totalFollowing)}, followers ${fmtStat(job?.totalFollowers)}`
                : `Phase 2 scan finished — ${(job?.scansSuccess ?? 0).toLocaleString()} OK, ${(job?.scansFailed ?? 0).toLocaleString()} failed`}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
          {isDone ? (
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => { onRefreshTable?.(); onClose(); }}
            >
              Close
            </button>
          ) : (
            <p className="text-xs text-gray-400 mr-auto self-center">Keep this open to watch live progress</p>
          )}
        </div>
      </div>
    </div>
  );
}

const VAFollowbackTab = ({ initialAgentId = null }) => {
  const PERIODS = [
    { id: 'all', label: 'All' },
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7d', label: 'Last 7 days' },
    { id: 'custom', label: 'Custom' }
  ];
  const [period, setPeriod] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [agentsData, setAgentsData] = useState({ agents: [], overall: {}, checkerPool: {}, period: null });
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  const [accountsByAgent, setAccountsByAgent] = useState({});
  const [accountsLoading, setAccountsLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [ledgerByAgent, setLedgerByAgent] = useState({});
  const [activeScanJob, setActiveScanJob] = useState(null);
  const pollRef = useRef(null);

  const periodParams = useCallback(() => {
    const params = { period };
    if (period === 'custom') {
      if (customFrom) params.from = customFrom;
      if (customTo) params.to = customTo;
    }
    return params;
  }, [period, customFrom, customTo]);

  const fetchAgents = useCallback(async ({ lite = false } = {}) => {
    const res = await api.get('/followback-health/agents', {
      params: { ...periodParams(), ...(lite ? { lite: 1 } : {}) }
    });
    setAgentsData((prev) => {
      const incoming = res.data.agents || [];
      if (lite && prev.agents?.length) {
        const byId = new Map(incoming.map((a) => [a.agentId, a]));
        return {
          ...prev,
          checkerPool: res.data.checkerPool || prev.checkerPool,
          agents: prev.agents.map((a) => {
            const next = byId.get(a.agentId);
            if (!next) return a;
            return {
              ...a,
              ...next,
              periodStats: a.periodStats,
              healthSummary: a.healthSummary,
              lastPhase2: next.lastPhase2 || a.lastPhase2,
              lastTest: next.lastTest || a.lastTest
            };
          })
        };
      }
      return {
        agents: incoming,
        overall: res.data.overall || {},
        checkerPool: res.data.checkerPool || {},
        period: res.data.period || null
      };
    });
  }, [periodParams]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await fetchAgents({ lite: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load follow-back data');
    } finally {
      setLoading(false);
    }
    fetchAgents().catch((err) => {
      toast.error(err.response?.data?.error || 'Failed to load follow-back stats');
    });
  }, [fetchAgents]);

  useEffect(() => {
    refreshAll();
    const interval = setInterval(() => {
      fetchAgents({ lite: true }).catch(() => {});
    }, 30_000);
    return () => clearInterval(interval);
  }, [refreshAll, fetchAgents]);

  const hasActiveScanner = (agentsData.agents || []).some(
    (a) => a.scanner?.active && !a.scanner?.adminPaused
  );

  useEffect(() => {
    if (!hasActiveScanner) return undefined;
    const fast = setInterval(() => {
      fetchAgents({ lite: true }).catch(() => {});
    }, 15_000);
    return () => clearInterval(fast);
  }, [hasActiveScanner, fetchAgents]);

  const pollScanJob = useCallback(async (jobId, kind = 'phase2') => {
    try {
      const path = kind === 'test'
        ? `/followback-health/test-scan/${jobId}`
        : `/followback-health/phase2-scan/${jobId}`;
      const res = await api.get(path);
      if (res.data?.job) setActiveScanJob(res.data.job);
      return res.data?.job;
    } catch (err) {
      console.error('Scan job poll failed:', err);
      return null;
    }
  }, []);

  const fetchAccountsForAgent = useCallback(async (agentId) => {
    setAccountsLoading((prev) => ({ ...prev, [agentId]: true }));
    try {
      const res = await api.get('/followback-health/accounts', {
        params: { agentId, limit: 200, sort: 'last_scan_desc' }
      });
      setAccountsByAgent((prev) => ({ ...prev, [agentId]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to load accounts for agent`);
    } finally {
      setAccountsLoading((prev) => ({ ...prev, [agentId]: false }));
    }
  }, []);

  useEffect(() => {
    if (!initialAgentId) return;
    const id = String(initialAgentId);
    setExpandedAgentId(id);
    fetchAccountsForAgent(id);
  }, [initialAgentId, fetchAccountsForAgent]);

  useEffect(() => {
    if (!activeScanJob?.jobId) return undefined;
    if (activeScanJob.status !== 'completed' && activeScanJob.status !== 'failed') {
      pollRef.current = setInterval(() => {
        pollScanJob(activeScanJob.jobId, activeScanJob.kind);
      }, 2000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
    refreshAll();
    if (expandedAgentId) fetchAccountsForAgent(expandedAgentId);
    return undefined;
  }, [activeScanJob?.jobId, activeScanJob?.status, activeScanJob?.kind, pollScanJob, refreshAll, expandedAgentId, fetchAccountsForAgent]);

  const toggleAgentExpand = (agentId) => {
    if (expandedAgentId === agentId) {
      setExpandedAgentId(null);
      return;
    }
    setExpandedAgentId(agentId);
    if (!accountsByAgent[agentId]) {
      fetchAccountsForAgent(agentId);
    }
  };

  useEffect(() => {
    api.get('/scraper-v2/workspaces')
      .then((res) => {
        if (res.data?.success) setWorkspaces(res.data.workspaces || []);
      })
      .catch(() => {});
  }, []);

  const handleScannerAction = async (agentId, action) => {
    const key = `${action}-${agentId}`;
    setActionLoading(key);
    try {
      const res = await api.post('/followback-health/scanner', {
        agentId,
        action,
        workspaceId: ledgerByAgent[agentId] || 'legacy',
      });
      const status = res.data?.status;
      if (status) {
        setAgentsData((prev) => ({
          ...prev,
          agents: (prev.agents || []).map((a) => (
            a.agentId === agentId ? { ...a, scanner: { ...a.scanner, ...status } } : a
          ))
        }));
      }
      const startedLabel = action === 'start2'
        ? 'Start 2 (P2-only) started'
        : action === 'start'
          ? 'Start (P1→P2) started'
          : 'paused';
      toast.success(`Scanner ${startedLabel} for agent`);
      fetchAgents({ lite: true }).catch(() => {});
      if (expandedAgentId === agentId) fetchAccountsForAgent(agentId).catch(() => {});
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} scanner`);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePhase2Scan = async (agentId = null) => {
    const scope = agentId ? 'this agent' : 'all agents';
    if (!window.confirm(`Run forced Phase 2 follower scrape for ${scope}? This may take several minutes.`)) return;
    const key = agentId ? `phase2-${agentId}` : 'phase2-all';
    setActionLoading(key);
    try {
      const res = await api.post('/followback-health/phase2-scan', { agentId: agentId || undefined });
      const job = res.data?.job || res.data;
      if (job?.jobId) {
        setActiveScanJob(job);
        pollScanJob(job.jobId, job.kind || 'phase2');
      } else {
        toast.success(`Phase 2 scan queued for ${scope}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to queue Phase 2 scan');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTestScan = async (agentId) => {
    if (!window.confirm(
      'Run test scan (Phase 1 → Phase 2)? Phase 1 checks following/followers on every account; '
      + 'Phase 2 runs only where followers increased since the last check.'
    )) return;
    const key = `test-${agentId}`;
    setActionLoading(key);
    try {
      const res = await api.post('/followback-health/test-scan', { agentId });
      const job = res.data?.job || res.data;
      if (job?.jobId) {
        setActiveScanJob(job);
        pollScanJob(job.jobId, 'test');
        if (!expandedAgentId || expandedAgentId !== agentId) {
          setExpandedAgentId(agentId);
        }
        fetchAccountsForAgent(agentId);
      } else {
        toast.success('Test scan queued');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to queue test scan');
    } finally {
      setActionLoading(null);
    }
  };

  const closeScanJobModal = () => setActiveScanJob(null);

  const overall = agentsData.overall || {};
  const periodLabel = agentsData.period?.label
    || PERIODS.find((p) => p.id === period)?.label
    || 'All';
  const scanTotals = {
    success: overall.scansSuccess ?? 0,
    failed: overall.scansFailed ?? 0
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <SignalIcon className="w-5 h-5 text-indigo-500" />
            Follow-back automation
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {agentsData.checkerPool?.active ?? 0} checker account(s) in pool · stats for {periodLabel}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm inline-flex items-center gap-2">
            <span className="text-gray-500">Period</span>
            <select
              className="form-select text-sm"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </label>
          {period === 'custom' && (
            <>
              <input
                type="date"
                className="form-input text-sm"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <input
                type="date"
                className="form-input text-sm"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </>
          )}
          <button
            type="button"
            onClick={() => handlePhase2Scan(null)}
            disabled={!!actionLoading}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
            {actionLoading === 'phase2-all' ? 'Queuing…' : 'Phase 2 scan — all agents'}
          </button>
          <button
            type="button"
            onClick={refreshAll}
            disabled={loading}
            className="btn-secondary text-sm flex items-center gap-1.5"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Overall totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat
          label="Active scanners"
          value={overall.activeScanners}
          sub={`${agentsData.agents.length} agent(s) total`}
          variant="indigo"
        />
        <MiniStat
          label={`Scans (${periodLabel})`}
          value={scanTotals.success}
          sub={`${scanTotals.failed} failed`}
          variant="emerald"
        />
        <MiniStat
          label="Follow-backs detected"
          value={overall.followBacksDetected ?? 0}
          sub={`${overall.dmsSent ?? 0} DMs sent · ${overall.dmsFailed ?? 0} failed`}
          variant="indigo"
        />
        <MiniStat
          label="Ejected accounts"
          value={overall.ejected}
          sub="Removed from follow automation"
          variant="red"
        />
      </div>

      {/* Agents table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Per agent</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Accounts column is logged-in only (same as /dashboard/accounts). Scans / FB / DM columns cover {periodLabel} (entire automation runtime when All). Expand a row for per-account detail.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
              <tr>
                <th className="text-left py-2.5 pl-4 pr-2 w-8" />
                <th className="text-left py-2.5 pr-4 font-medium">Agent</th>
                <th className="text-left py-2.5 pr-4 font-medium">Scanner</th>
                <th className="text-left py-2.5 pr-4 font-medium">Last scan</th>
                <th className="text-right py-2.5 pr-4 font-medium" title="Wingr Account status = active">Logged-in</th>
                <th className="text-right py-2.5 pr-4 font-medium" title="Follower list fetches in selected period">Scans OK</th>
                <th className="text-right py-2.5 pr-4 font-medium" title="Failed follower list fetches in selected period">Failed</th>
                <th className="text-right py-2.5 pr-4 font-medium">Ledger FB</th>
                <th className="text-right py-2.5 pr-4 font-medium">Organic</th>
                <th className="text-right py-2.5 pr-4 font-medium">DMs OK</th>
                <th className="text-right py-2.5 pr-4 font-medium">DMs fail</th>
                <th className="text-left py-2.5 pr-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
              {agentsData.agents.map((agent) => {
                const scanner = agent.scanner || {};
                const stats = agent.periodStats || null;
                const p2 = agent.lastPhase2 || null;
                const lastTest = agent.lastTest || null;
                const isExpanded = expandedAgentId === agent.agentId;
                const accountsData = accountsByAgent[agent.agentId];
                const isLoadingAccounts = accountsLoading[agent.agentId];

                return (
                  <React.Fragment key={agent.agentId}>
                    <tr className="hover:bg-gray-50/60 dark:hover:bg-gray-900/20">
                      <td className="py-2.5 pl-4 pr-2">
                        <button
                          type="button"
                          onClick={() => toggleAgentExpand(agent.agentId)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          {isExpanded
                            ? <ChevronDownIcon className="w-4 h-4" />
                            : <ChevronRightIcon className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="font-medium text-gray-900 dark:text-white">{agent.name}</div>
                        <div className="text-xs text-gray-500">
                          {agent.status === 'running' ? 'Follow automation running' : agent.status}
                          {p2?.finishedAt && (
                            <span className="ml-2 text-indigo-500 dark:text-indigo-400">
                              · Phase 2 {timeAgo(p2.finishedAt)}
                            </span>
                          )}
                          {lastTest?.finishedAt && (
                            <span className="ml-2 text-amber-600 dark:text-amber-400">
                              · Test {timeAgo(lastTest.finishedAt)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                          scanner.adminPaused
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                            : scanner.active
                              ? scanner.scanning
                                ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
                                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${
                            scanner.adminPaused
                              ? 'bg-amber-500'
                              : scanner.active
                                ? scanner.scanning ? 'bg-sky-500 animate-pulse' : 'bg-emerald-500'
                                : 'bg-gray-400'
                          }`} />
                          {scanner.adminPaused
                            ? 'Paused (admin)'
                            : scanner.active
                              ? (
                                (scanner.scanning ? 'Scanning' : 'Running')
                                + (scanner.mode === 'p2' ? ' · P2' : '')
                              )
                              : 'Stopped'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300" title={formatDateTime(scanner.lastScanAt)}>
                        {timeAgo(scanner.lastScanAt)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums">{agent.accountCount}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                        {fmtStat(stats?.scansSuccess)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-red-500">
                        {fmtStat(stats?.scansFailed)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-indigo-600 dark:text-indigo-400">
                        {fmtStat(stats?.ledgerFollowBacks)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-sky-600 dark:text-sky-400">
                        {fmtStat(stats?.organicFollowers)}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                        {fmtStat((stats?.dmsSentLedger || 0) + (stats?.dmsSentOrganic || 0))}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-red-500">
                        {fmtStat(stats?.dmsFailed ?? ((stats?.dmsFailedLedger || 0) + (stats?.dmsFailedOrganic || 0)))}
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="flex flex-wrap gap-1.5">
                          {scanner.active && !scanner.adminPaused ? (
                            <button
                              type="button"
                              disabled={!!actionLoading}
                              onClick={() => handleScannerAction(agent.agentId, 'stop')}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300 disabled:opacity-50"
                            >
                              <StopIcon className="w-3.5 h-3.5" />
                              Stop
                            </button>
                          ) : (
                            <>
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                {agent.modelName || 'Model'}
                              </span>
                              <select
                                className="text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-1.5 py-1 max-w-[14rem]"
                                value={ledgerByAgent[agent.agentId] || 'legacy'}
                                onChange={(e) => setLedgerByAgent((prev) => ({
                                  ...prev,
                                  [agent.agentId]: e.target.value,
                                }))}
                                title="Follow ledger this checker will scan"
                              >
                                {(workspaces.length ? workspaces : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]).map((w) => (
                                  <option key={w.workspaceId} value={w.workspaceId}>
                                    {w.name} — {(Number(w.followBacks) || 0).toLocaleString()} FB
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                disabled={!!actionLoading}
                                onClick={() => handleScannerAction(agent.agentId, 'start')}
                                title="P1 stats gate, then P2 where followers rose / ledger backlog"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 disabled:opacity-50"
                              >
                                <PlayIcon className="w-3.5 h-3.5" />
                                Start
                              </button>
                              <button
                                type="button"
                                disabled={!!actionLoading}
                                onClick={() => {
                                  if (!window.confirm(
                                    'Start 2 (P2-only)? Keeps the shared scan pool full (750 in flight), '
                                    + 'pulls the next account as soon as a slot frees, waits 3–5 min after a full lap, skips P1, and DMs on followbacks.'
                                  )) return;
                                  handleScannerAction(agent.agentId, 'start2');
                                }}
                                title="Continuous Phase 2 pool (750 in flight), 3–5 min rest after each full lap + DM — skips broken P1 stats gate"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-teal-50 text-teal-800 hover:bg-teal-100 dark:bg-teal-900/20 dark:text-teal-200 disabled:opacity-50"
                              >
                                <PlayIcon className="w-3.5 h-3.5" />
                                Start 2
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            disabled={!!actionLoading}
                            onClick={() => handleTestScan(agent.agentId)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-200 disabled:opacity-50"
                          >
                            <BeakerIcon className="w-3.5 h-3.5" />
                            Test P1→P2
                          </button>
                          <button
                            type="button"
                            disabled={!!actionLoading}
                            onClick={() => handlePhase2Scan(agent.agentId)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 disabled:opacity-50"
                          >
                            <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                            Phase 2
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={12} className="p-0 bg-gray-50/50 dark:bg-gray-900/30">
                          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                            {lastTest && (
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs">
                                <span className="text-amber-700 dark:text-amber-300">
                                  Last test: P1 {(lastTest.phase1Checked ?? 0).toLocaleString()} checked · P2 {(lastTest.phase2Triggered ?? 0).toLocaleString()} triggered
                                </span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  Following <span className="tabular-nums font-semibold">{fmtStat(lastTest.totalFollowing)}</span>
                                  {' · '}
                                  Followers <span className="tabular-nums font-semibold">{fmtStat(lastTest.totalFollowers)}</span>
                                </span>
                                {(lastTest.totalFollowingDelta != null || lastTest.totalFollowersDelta != null) && (
                                  <span className="text-gray-600 dark:text-gray-400">
                                    <span className={deltaClass(lastTest.totalFollowingDelta)}>
                                      Δ Following {fmtDelta(lastTest.totalFollowingDelta)}
                                    </span>
                                    {' · '}
                                    <span className={deltaClass(lastTest.totalFollowersDelta)}>
                                      Δ Followers {fmtDelta(lastTest.totalFollowersDelta)}
                                    </span>
                                  </span>
                                )}
                              </div>
                            )}
                            {agent.periodStats && (
                              <div className="flex flex-wrap gap-4 mb-3 text-xs">
                                <span className="text-gray-700 dark:text-gray-300 font-medium">
                                  Period ({periodLabel}):
                                </span>
                                <span className="text-indigo-600 dark:text-indigo-400">
                                  ledger {fmtStat(agent.periodStats.ledgerFollowBacks)}, organic {fmtStat(agent.periodStats.organicFollowers)}
                                </span>
                                <span className="text-emerald-600 dark:text-emerald-400">
                                  DMs {fmtStat((agent.periodStats.dmsSentLedger || 0) + (agent.periodStats.dmsSentOrganic || 0))} OK
                                </span>
                                <span className="text-red-500">
                                  DMs {fmtStat(agent.periodStats.dmsFailed)} failed
                                </span>
                                <span className="text-gray-500">
                                  {fmtStat(agent.periodStats.scansSuccess)} scans OK · {fmtStat(agent.periodStats.scansFailed)} failed
                                </span>
                              </div>
                            )}
                            {p2 && (
                              <div className="flex flex-wrap gap-4 mb-3 text-xs">
                                <span className="text-indigo-600 dark:text-indigo-400">
                                  Last Phase 2: ledger {fmtStat(p2.ledgerFollowBacks)}, organic {fmtStat(p2.organicFollowers)}
                                </span>
                                <span className="text-emerald-600 dark:text-emerald-400">
                                  DMs {fmtStat((p2.dmsSentLedger || 0) + (p2.dmsSentOrganic || 0))} OK
                                </span>
                                <span className="text-red-500">
                                  DMs {fmtStat((p2.dmsFailedLedger || 0) + (p2.dmsFailedOrganic || 0))} failed
                                </span>
                                <span className="text-gray-500">
                                  {fmtStat(p2.scansSuccess)} scans OK · {fmtStat(p2.scansFailed)} failed
                                </span>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-500 dark:text-gray-400">
                              <span>{accountsData?.totals?.inAutomation ?? 0} in follow automation</span>
                              <span>{accountsData?.totals?.pendingChecks ?? 0} pending ledger checks</span>
                            </div>
                            {isLoadingAccounts && (
                              <div className="py-6 text-center text-sm text-gray-500">Loading accounts…</div>
                            )}
                            {!isLoadingAccounts && (
                              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <table className="min-w-full text-xs">
                                  <thead className="text-gray-400 uppercase border-b border-gray-100 dark:border-gray-700">
                                    <tr>
                                      <th className="text-left py-2 px-3 font-medium">Account</th>
                                      <th className="text-left py-2 px-3 font-medium">Last scan</th>
                                      <th className="text-left py-2 px-3 font-medium">Phase</th>
                                      <th className="text-right py-2 px-3 font-medium">Following</th>
                                      <th className="text-right py-2 px-3 font-medium">Followers</th>
                                      <th className="text-right py-2 px-3 font-medium">Δ Following</th>
                                      <th className="text-right py-2 px-3 font-medium">Δ Followers</th>
                                      <th className="text-right py-2 px-3 font-medium">Follow-backs</th>
                                      <th className="text-right py-2 px-3 font-medium">Pending</th>
                                      <th className="text-right py-2 px-3 font-medium">DMs</th>
                                      <th className="text-left py-2 px-3 font-medium">Error</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                                    {(accountsData?.accounts || []).map((a) => {
                                      const badge = phaseBadge(
                                        a.followBackHealth.lastScanPhase,
                                        a.followBackHealth.lastPhase2Success
                                      );
                                      const fh = a.followBackHealth || {};
                                      return (
                                        <tr key={a.accountId}>
                                          <td className="py-2 px-3 font-mono text-gray-900 dark:text-white">
                                            @{a.username}
                                          </td>
                                          <td className="py-2 px-3 text-gray-600 dark:text-gray-300" title={formatDateTime(fh.lastScanAt)}>
                                            {timeAgo(fh.lastScanAt)}
                                          </td>
                                          <td className="py-2 px-3">
                                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.cls}`}>
                                              {badge.label}
                                            </span>
                                          </td>
                                          <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                                            {fmtStat(fh.lastRawFollowing ?? a.followingCount)}
                                          </td>
                                          <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                                            {fmtStat(fh.lastRawFollowers ?? a.followersCount)}
                                          </td>
                                          <td className={`py-2 px-3 text-right tabular-nums ${deltaClass(fh.followingDelta)}`}>
                                            {fmtDelta(fh.followingDelta)}
                                          </td>
                                          <td className={`py-2 px-3 text-right tabular-nums ${deltaClass(fh.followersDelta)}`}>
                                            {fmtDelta(fh.followersDelta)}
                                          </td>
                                          <td className="py-2 px-3 text-right tabular-nums">
                                            {(a.ledger.totalFollowBacks || 0).toLocaleString()}
                                          </td>
                                          <td className="py-2 px-3 text-right tabular-nums">
                                            {(a.ledger.pendingChecks || 0).toLocaleString()}
                                          </td>
                                          <td className="py-2 px-3 text-right tabular-nums">
                                            {(a.ledger.dmsSent || 0).toLocaleString()}
                                          </td>
                                          <td className="py-2 px-3 max-w-[160px] truncate text-red-600 dark:text-red-400" title={fh.lastError}>
                                            {fh.lastError || '—'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {(accountsData?.accounts || []).length === 0 && (
                                      <tr>
                                        <td colSpan={11} className="py-6 text-center text-gray-500">No accounts</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!loading && agentsData.agents.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-10 text-center text-gray-500 dark:text-gray-400">
                    No AI VA agents found.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={12} className="py-10 text-center text-gray-500 dark:text-gray-400">
                    Loading follow-back status…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {activeScanJob && (
        <ScanJobProgressModal
          job={activeScanJob}
          onClose={closeScanJobModal}
          onRefreshTable={refreshAll}
        />
      )}
    </div>
  );
};

export default VAFollowbackTab;
